import { Box, CircularProgress, Stack } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

const DEFAULT_ANIMATION_DURATION = 30 * 1000; // 3 minutes
const DEFAULT_MIN_VALUE = 1;
const THICKNESS = 4;

export interface AnimatedCircularProgressRef {
  /**
   * Start the animation. Takes an optional progress value to start from.
   */
  start: (value?: number) => void;
  /**
   * Stop the animation.
   */
  stop: () => void;
  /**
   * Stop the animation and set the progress to 100%.
   */
  complete: () => void;
  /**
   * Stop the animation and set the progress to 0%.
   */
  reset: () => void;
  /**
   * Set the progress value directly.
   */
  setValue: (value: number) => void;

}

export interface AnimatedCircularProgressProps {
  /**
   * Size of the circular progress indicator.
   */
  size: number;
  /**
   * Duration of the animation in milliseconds. Default is 3 minutes.
   */
  animationDuration?: number;
}

const AnimatedCircularProgress = forwardRef<
  AnimatedCircularProgressRef,
  AnimatedCircularProgressProps
>(({ size, animationDuration = DEFAULT_ANIMATION_DURATION }, ref) => {
  const [internalValue, setInternalValue] = useState(0);
  const externalValueRef = useRef(0);
  const animationStartTimeRef = useRef(0);
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const startAnimationInterval = (startValue: number) => {
    const maxValue = 99;

    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
    }

    animationIntervalRef.current = setInterval(() => {
      const elapsedTime = Date.now() - animationStartTimeRef.current;

      if (elapsedTime >= animationDuration) {
        setInternalValue(maxValue);
        stopAnimationInterval();
        return;
      }

      const newValue = Math.max(
        startValue,
        Math.min(maxValue, Math.floor((elapsedTime / animationDuration) * maxValue)),
      );

      if (newValue > externalValueRef.current) {
        setInternalValue(newValue);
      }
    }, 100);
  };

  const stopAnimationInterval = () => {
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }
  };

  const start = (value = DEFAULT_MIN_VALUE) => {
    setValue(0);
    animationStartTimeRef.current = Date.now();
    startAnimationInterval(value);
  };

  const stop = () => {
    stopAnimationInterval();
  };

  const complete = () => {
    setInternalValue(100);
    stopAnimationInterval();
  };

  const reset = () => {
    setInternalValue(0);
    stopAnimationInterval();
  };

  const setValue = (value: number) => {
    externalValueRef.current = value;

    if (value > internalValue) {
      setInternalValue(value);
    }
  };

  useImperativeHandle(ref, () => ({
    start,
    stop,
    complete,
    reset,
    setValue,
  }));

  useEffect(() => {
    // Start the animation when the component mounts
    start();

    return () => {
      // Clean up when the component unmounts
      reset();
    };
  }, []);

  return (
    <Stack spacing={5} flexGrow={1} pt={8}>
      <Box sx={{ alignItems: 'center', textAlign: 'center' }}>
        <Box position="relative" display="inline-flex">
          <CircularProgress
            variant="determinate"
            value={100}
            size={size}
            thickness={THICKNESS}
            sx={{
              color: 'background.neutral',
              position: 'absolute',
            }}
          />
          <CircularProgress
            variant="determinate"
            value={internalValue}
            size={size}
            thickness={THICKNESS}
            sx={{
              color: 'success2.main',
              strokeLinecap: 'round',
            }}
          />
        </Box>
      </Box>
    </Stack>
  );
});

export default AnimatedCircularProgress;
